import React from 'react';
import {
  Modal,
  InputNumber,
  Input,
  Button,
  Form,
  Typography,
  Row,
  Col,
  Card,
  Select
} from 'antd';
import { debtService, Debt } from '../../services/debtService';
import { DollarOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface Props {
  debt: Debt;
  onClose: () => void;
  onPaid: () => void;
  debts?: Debt[];
}

const DebtPaymentModal: React.FC<Props> = ({ debt, onClose, onPaid, debts }) => {
  const [form] = Form.useForm();
  const [selectedDebtId, setSelectedDebtId] = React.useState<number>(debt.id);
  const debtList = debts && debts.length > 0 ? debts : [debt];
  const selectedDebt = debtList.find(d => d.id === selectedDebtId) || debt;
  
  // Tính tổng số nợ còn lại của tất cả các phiếu công nợ (để hỗ trợ tự động cấn trừ FIFO)
  const totalRemaining = debtList.reduce((sum, d) => {
    return sum + (d.debt_amount - d.paid_amount);
  }, 0);
  
  const remain = selectedDebt.debt_amount - selectedDebt.paid_amount;

  React.useEffect(() => {
    // Nếu selectedDebtId không còn trong danh sách nợ còn lại, tự động chọn khoản nợ đầu tiên
    if (debtList.length > 0 && !debtList.some(d => d.id === selectedDebtId)) {
      setSelectedDebtId(debtList[0].id);
    }
  }, [debtList, selectedDebtId]);

  const handleFinish = async (values: any) => {
    // Backend sẽ tự động cấn trừ từ phiếu cũ nhất (FIFO) dựa trên customer_id
    await debtService.createPayment(selectedDebt.id, values.amount, values.note);
    onPaid();
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={
        <Title level={4} style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
          Thanh toán công nợ
        </Title>
      }
      width={600}
      style={{ top: 100 }}
    >
      <div style={{ padding: '8px 0' }}>
        {debtList.length > 1 && (
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
              marginBottom: 24
            }}
            bodyStyle={{ padding: 20 }}
          >
            <Form.Item
              label={
                <Text strong style={{ color: '#1e293b', fontSize: 14 }}>
                  Chọn khoản nợ
                </Text>
              }
              style={{ marginBottom: 0 }}
            >
              <Select
                value={selectedDebtId}
                onChange={(value) => setSelectedDebtId(value)}
                style={{ width: '100%' }}
                size="large"
              >
                {debtList.map(d => (
                  <Option key={d.id} value={d.id}>
                    Nợ #{d.id} - {d.debt_amount.toLocaleString('vi-VN')}đ (Còn lại: {(d.debt_amount - d.paid_amount).toLocaleString('vi-VN')}đ)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Card>
        )}

        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
            marginBottom: 24
          }}
          bodyStyle={{ padding: 20 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ 
                padding: '16px',
                background: '#fef2f2',
                borderRadius: 12,
                border: '1px solid #fee2e2'
              }}>
                <Text style={{ 
                  fontSize: 11, 
                  fontWeight: 600, 
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'block',
                  marginBottom: 8
                }}>
                  Còn lại (phiếu này)
                </Text>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Text style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: '#ef4444' 
                  }}>
                    {remain.toLocaleString('vi-VN')}
                  </Text>
                  <Text style={{ fontSize: 12, color: '#94a3b8' }}>đ</Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ 
                padding: '16px',
                background: '#ecfdf5',
                borderRadius: 12,
                border: '1px solid #a7f3d0'
              }}>
                <Text style={{ 
                  fontSize: 11, 
                  fontWeight: 600, 
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'block',
                  marginBottom: 8
                }}>
                  Tổng nợ còn lại
                </Text>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Text style={{ 
                    fontSize: 24, 
                    fontWeight: 700, 
                    color: '#10b981' 
                  }}>
                    {totalRemaining.toLocaleString('vi-VN')}
                  </Text>
                  <Text style={{ fontSize: 12, color: '#94a3b8' }}>đ</Text>
                </div>
              </div>
            </Col>
          </Row>
          {debtList.length > 1 && (
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              background: '#eff6ff', 
              borderRadius: 8, 
              border: '1px solid #bfdbfe' 
            }}>
              <Text style={{ fontSize: 12, color: '#1e40af' }}>
                <strong>Lưu ý:</strong> Hệ thống sẽ tự động cấn trừ từ phiếu nợ cũ nhất đến khi hết số tiền thanh toán (nguyên tắc FIFO).
              </Text>
            </div>
          )}
        </Card>

        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleFinish} 
          initialValues={{ amount: Math.min(remain, totalRemaining) }}
        >
          <Form.Item
            label={
              <Text strong style={{ color: '#1e293b', fontSize: 14 }}>
                Số tiền thanh toán
              </Text>
            }
            name="amount"
            rules={[
              { required: true, message: 'Nhập số tiền!' },
              { 
                type: 'number', 
                min: 1, 
                max: totalRemaining, 
                message: `Tối đa ${totalRemaining.toLocaleString('vi-VN')}đ (tổng nợ còn lại)` 
              }
            ]}
          >
            <InputNumber
              min={1}
              max={totalRemaining}
              style={{ width: '100%' }}
              size="large"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="Nhập số tiền"
            />
          </Form.Item>

          <Form.Item
            label={
              <Text strong style={{ color: '#1e293b', fontSize: 14 }}>
                Ghi chú
              </Text>
            }
            name="note"
          >
            <Input.TextArea
              rows={3}
              maxLength={200}
              placeholder="Nhập ghi chú (tùy chọn)"
              style={{
                borderRadius: 12,
                border: '1px solid #e2e8f0'
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              style={{
                height: 48,
                borderRadius: 12,
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                fontSize: 16
              }}
            >
              Thanh toán
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default DebtPaymentModal;
